/**
 * Which session a teacher-view quick story attaches to.
 * In class on the selected session (the same test as the Present list,
 * including ENROLLED) uses that session. Absent and planned-absent fall
 * back to the nearest session already marked attended. This only chooses
 * the story's classSessionId. It does not write attendance.
 */
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { AttendanceStatus } from "#customTypes/types"

export type StoryAttachmentSource = "roster" | "makeup" | "tryout"

const ROSTER_IN_CLASS: Partial<Record<AttendanceStatus, true>> = {
  [ATTENDANCE_STATUSES.PRESENT]: true,
  [ATTENDANCE_STATUSES.TARDY]: true,
  [ATTENDANCE_STATUSES.ENROLLED]: true,
}

const MAKEUP_IN_CLASS: Partial<Record<AttendanceStatus, true>> = {
  [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: true,
  [ATTENDANCE_STATUSES.MAKEUP_TARDY]: true,
  [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: true,
}

const TRYOUT_IN_CLASS: Partial<Record<AttendanceStatus, true>> = {
  [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: true,
  [ATTENDANCE_STATUSES.TRYOUT_TARDY]: true,
  [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: true,
}

const IN_CLASS_BY_SOURCE: Record<
  StoryAttachmentSource,
  Partial<Record<AttendanceStatus, true>>
> = {
  roster: ROSTER_IN_CLASS,
  makeup: MAKEUP_IN_CLASS,
  tryout: TRYOUT_IN_CLASS,
}

export const countsAsInClass = (
  source: StoryAttachmentSource,
  status: AttendanceStatus
): boolean => IN_CLASS_BY_SOURCE[source][status] === true

export const teacherStorySessionId = (
  inClassSessionId: string | undefined,
  nearestAttendedId: string | undefined
): string | undefined => inClassSessionId ?? nearestAttendedId

/**
 * Roster rows are recorded before makeups and tryouts. An absent roster cell
 * for this session must not hide a later in-class makeup or tryout on it.
 */
export const entryForSelectedSession = <
  T extends { sessionId: string; isInClass: boolean },
>(
  entries: readonly T[],
  selectedSessionId: string | undefined
): T | undefined => {
  const selected = entries.filter(
    (entry) => entry.sessionId === selectedSessionId
  )
  return selected.find((entry) => entry.isInClass) ?? selected[0]
}
